<template>
	<div
		class="offer__label"
		v-if="promotionOffer"
		:class="{
			'v-2': buyRentExpVariant === 'variant_2',
			'v-3': buyRentExpVariant === 'variant_3',
		}"
	>
		<span class="offer__label__free-trial" :class="{ 'text-grey': isHighlighted }">
			{{ freeTrialText }}
		</span>
		<span class="offer__label__price">
			<span v-if="retailPrice">{{ retailPrice }}</span>
			<!-- This will still make sure the space is still taken up (it's transparent) -->
			<span v-else-if="!localisedCountry" class="offer__label__price transparent">retail price</span>
			<img v-if="localisedCountry" class="icon-flag" :src="flagSrc" :alt="localisedCountry" />
		</span>
	</div>
</template>

<script lang="ts" setup>
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { computed } from 'vue';
import { PlanOffer } from '@/features/buybox/components/list/ListPromotionRow.vue';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { useExperiment } from '@/libraries/experiment';

const props = defineProps<{
	title: TitleDetail;
	promotionOffer: TitleOfferFragment | null;
	packagePlan: PlanOffer | null;
	isHighlighted?: boolean;
	localisedCountry?: string | null;
}>();

const { t } = useI18n();
const { activeVariantWithControlGroup: buyRentExpVariant } = useExperiment('EXP_MERGE_BUY_RENT');

const retailPrice = computed(() => {
	if (props.packagePlan?.retailPrice) return t('WEBAPP_THEN_X_PER_MONTH', { price: props.packagePlan.retailPrice });
	return null;
});

const freeTrialText = computed(() => {
	// for providers which expect no packagePlan
	if (['jyn', 'jwt'].includes(props.promotionOffer?.package.shortName || '')) return t('WEBAPP_FREE');

	if (props.promotionOffer?.package.shortName === 'tbv') return t('WEBAPP_FREE_WITH_ADS');

	if (props.packagePlan?.durationDays) {
		return props.packagePlan.durationDays === 1
			? t('WEBAPP_1_DAY_FREE')
			: t('WEBAPP_X_DAYS_FREE', { days: props.packagePlan.durationDays });
	}
	return null;
});

const flagSrc = computed(() => {
	if (!props.localisedCountry) return undefined;
	const countryCode = props.localisedCountry === 'GB' ? 'UK' : props.localisedCountry;
	return `${JW_CONFIG.ASSETS_URL}/flags/${countryCode}.png`;
});
</script>

<style lang="scss" scoped>
.offer__label {
	/* Fallback for @container rule */
	@media (max-width: 750px) {
		justify-self: flex-start;
		max-width: calc(50vw - 32px);

		// variant 1
		text-align: start;
		// align-self: flex-end;
	}

	@container offer-container (max-width: 600px) {
		justify-self: flex-start;
		max-width: calc(50vw - 32px);

		// variant 1
		text-align: start;
		// align-self: flex-end;
	}

	&__free-trial {
		margin: 0;
		color: var(--jw-yellow-200);
		font-size: 14px;
		font-weight: 900;
		line-height: 16px;
		letter-spacing: 0.21px;
		margin-bottom: 4px;

		&.cta-gray {
			color: var(--jw-grey-200);
		}
	}

	&__price {
		margin: 0;
		color: var(--jw-grey-200);
		font-size: 14px;
		font-weight: 400;
		line-height: 24px;
		letter-spacing: 0.21px;
		display: -webkit-box;
		line-clamp: 2;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
	}
}

.transparent {
	color: transparent;
}

.free-trial-wrapper {
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	justify-content: flex-end;
}
.icon-flag {
	position: relative;
	width: 20px;
	left: 3px;
	top: -1px;
}

.v-2,
.v-3 {
	@media (max-width: 750px) {
		text-align: center;

		.offer__label__price {
			padding-top: 8px;
			line-height: 20px;
			line-clamp: 3;
			-webkit-line-clamp: 3;
		}
	}

	@container offer-container (max-width: 600px) {
		text-align: center;

		.offer__label__price {
			padding-top: 8px;
			line-height: 20px;
			line-clamp: 3;
			-webkit-line-clamp: 3;
		}
	}
}
</style>
